import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Modal, Empty, Spin } from 'antd';
import {
    SearchOutlined,
    UserOutlined,
    EnvironmentOutlined,
    ShopOutlined,
    RightOutlined,
    LoadingOutlined,
} from '@ant-design/icons';
import { FaFileInvoiceDollar, FaTrophy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import { AuthContext } from '../../../../contexts/AuthContext';
import { EModule } from '../../../../enums';
import SearchService, { IGlobalSearchResponse } from '../../../../services/http/services/SearchService';
import { formatCpf, formatPhone, formatCnpj, formatCpfCnpj } from '../../../../utils/Masks';
import './style.css';

export interface ISearchItem {
    id: number | string;
    title: string;
    subtitle: string;
    category: 'Usuários aplicativo' | 'Usuários (IPTU)' | 'Municípios' | 'Sorteios' | 'Empresas (Notas)';
    categoryKey: 'app_user' | 'iptu_user' | 'county' | 'giveaway' | 'company';
    badgeColor: string;
    icon: React.ReactNode;
    route: string;
}

function formatDateSafe(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    } catch {
        return '';
    }
}

interface IMapOptions {
    hasAutoModule: boolean;
    isAdmin: boolean;
}

function mapResponseToItems(data: IGlobalSearchResponse, options: IMapOptions): ISearchItem[] {
    const items: ISearchItem[] = [];

    if (data?.app_users && Array.isArray(data.app_users)) {
        data.app_users.forEach((user) => {
            const parts: string[] = [];
            if (user.cpf) parts.push(`CPF: ${formatCpf(user.cpf)}`);
            if (user.phone) parts.push(`Tel: ${formatPhone(user.phone)}`);
            if (user.email) parts.push(user.email);

            items.push({
                id: `user_${user.id}`,
                title: user.name || 'Usuário do aplicativo',
                subtitle: parts.length > 0 ? parts.join(' • ') : 'Usuário cadastrado no aplicativo',
                category: 'Usuários aplicativo',
                categoryKey: 'app_user',
                badgeColor: 'blue',
                icon: <UserOutlined style={{ color: '#1677ff' }} />,
                route: `/usuarios/aplicativo/visualizar/${user.id}`,
            });
        });
    }

    if (options.hasAutoModule && data?.iptu_users && Array.isArray(data.iptu_users)) {
        data.iptu_users.forEach((user) => {
            const parts: string[] = [];
            if (user.cpfCnpj) parts.push(`Doc: ${formatCpfCnpj(user.cpfCnpj)}`);

            items.push({
                id: `iptu_${user.id}`,
                title: user.name || 'Contribuinte IPTU',
                subtitle: parts.length > 0 ? parts.join(' • ') : 'Contribuinte cadastrado',
                category: 'Usuários (IPTU)',
                categoryKey: 'iptu_user',
                badgeColor: 'orange',
                icon: <FaFileInvoiceDollar color="#fa8c16" />,
                route: `/usuarios/auto/${user.id}`,
            });
        });
    }

    if (options.isAdmin && data?.cities && Array.isArray(data.cities)) {
        data.cities.forEach((city) => {
            const parts: string[] = [];
            if (city.cnpj) parts.push(`CNPJ: ${formatCnpj(city.cnpj)}`);
            if (city.name && city.city && city.name !== city.city) parts.push(city.name);

            items.push({
                id: `city_${city.id}`,
                title: `${city.city || city.name || 'Município'} - ${city.uf || ''}`,
                subtitle: parts.length > 0 ? parts.join(' • ') : 'Município cadastrado',
                category: 'Municípios',
                categoryKey: 'county',
                badgeColor: 'cyan',
                icon: <EnvironmentOutlined style={{ color: '#13c2c2' }} />,
                route: `/municipios/editar/${city.id}`,
            });
        });
    }

    if (data?.giveaways && Array.isArray(data.giveaways)) {
        data.giveaways.forEach((g) => {
            const parts: string[] = [];
            if (g.status) parts.push(`Status: ${g.status}`);
            if (g.dateOfRaffle) {
                const formattedDate = formatDateSafe(g.dateOfRaffle);
                if (formattedDate) parts.push(`Data: ${formattedDate}`);
            }
            if (g.type) parts.push(`Tipo: ${g.type}`);

            items.push({
                id: `giveaway_${g.id}`,
                title: g.description || `Sorteio #${g.id}`,
                subtitle: parts.length > 0 ? parts.join(' • ') : 'Sorteio cadastrado',
                category: 'Sorteios',
                categoryKey: 'giveaway',
                badgeColor: 'gold',
                icon: <FaTrophy color="#faad14" />,
                route: `/sorteios/geral/visualizar/${g.id}`,
            });
        });
    }

    if (data?.companies && Array.isArray(data.companies)) {
        data.companies.forEach((comp) => {
            const parts: string[] = [];
            if (comp.cnpj) parts.push(`CNPJ: ${formatCnpj(comp.cnpj)}`);
            if (comp.city || comp.uf) {
                const location = [comp.city, comp.uf].filter(Boolean).join(' - ');
                if (location) parts.push(location);
            }

            items.push({
                id: `company_${comp.id}`,
                title: comp.name || 'Empresa Emissora',
                subtitle: parts.length > 0 ? parts.join(' • ') : 'Estabelecimento comercial',
                category: 'Empresas (Notas)',
                categoryKey: 'company',
                badgeColor: 'purple',
                icon: <ShopOutlined style={{ color: '#722ed1' }} />,
                route: `/notas/estabelecimento?company=${encodeURIComponent(comp.name || '')}`,
            });
        });
    }

    return items;
}

export default function GlobalSearch() {
    const { theme } = useContext(ThemeContext);
    const { user, activeCity } = useContext(AuthContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState<ISearchItem[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const hasAutoModule = useMemo(() => {
        if (!activeCity?.modules) return false;
        if (Array.isArray(activeCity.modules)) {
            return activeCity.modules.includes(EModule.TAXES_AUTO) || (activeCity.modules as any).includes('taxes_auto');
        }
        if (typeof activeCity.modules === 'string') {
            return (activeCity.modules as string).includes('taxes_auto');
        }
        return false;
    }, [activeCity]);

    const isAdmin = useMemo(() => {
        if (!user) return false;
        return (
            user.role?.id === 1 || user.role?.name?.toUpperCase() === 'ADMIN' || !!user.permissions?.some((p) => p.name === 'manage_county')
        );
    }, [user]);

    const categories = useMemo(() => {
        const list = [
            { label: 'Todos', value: 'all', icon: <SearchOutlined /> },
            { label: 'Usuários aplicativo', value: 'app_user', color: 'blue', icon: <UserOutlined /> },
        ];

        if (hasAutoModule) {
            list.push({
                label: 'Usuários (IPTU)',
                value: 'iptu_user',
                color: 'orange',
                icon: <FaFileInvoiceDollar />,
            });
        }

        if (isAdmin) {
            list.push({
                label: 'Municípios',
                value: 'county',
                color: 'cyan',
                icon: <EnvironmentOutlined />,
            });
        }

        list.push(
            { label: 'Sorteios', value: 'giveaway', color: 'gold', icon: <FaTrophy /> },
            { label: 'Empresas (Notas)', value: 'company', color: 'purple', icon: <ShopOutlined /> }
        );

        return list;
    }, [hasAutoModule, isAdmin]);

    useEffect(() => {
        const isCurrentCategoryAvailable = categories.some((c) => c.value === selectedCategory);
        if (!isCurrentCategoryAvailable) {
            setSelectedCategory('all');
        }
    }, [categories, selectedCategory]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setIsModalOpen((prev) => !prev);
            } else if (e.key === 'Escape' && isModalOpen) {
                setIsModalOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen]);

    useEffect(() => {
        if (isModalOpen) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        } else {
            setSearchTerm('');
            setSelectedCategory('all');
            setSelectedIndex(0);
            setResults([]);
            setHasSearched(false);
            setIsLoading(false);
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        }
    }, [isModalOpen]);

    const performSearch = useCallback(
        async (term: string, category: string, cityId?: number) => {
            const query = term.trim();


            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            if (query.length < 2) {
                setResults([]);
                setHasSearched(false);
                setIsLoading(false);
                return;
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            setIsLoading(true);
            setHasSearched(true);

            try {
                const limit = category === 'all' ? 6 : 20;
                const data = await SearchService.search({
                    q: query,
                    cityId,
                    category,
                    limit,
                    signal: controller.signal,
                });

                const items = mapResponseToItems(data, { hasAutoModule, isAdmin });
                setResults(items);
                setSelectedIndex(0);
            } catch (err: any) {
                if (axios.isCancel(err) || err?.name === 'CanceledError' || err?.name === 'AbortError') {
                    return;
                }
                console.error('Erro na busca global:', err);
                setResults([]);
            } finally {
                setIsLoading(false);
            }
        },
        [hasAutoModule, isAdmin]
    );

    useEffect(() => {
        if (!isModalOpen) return;

        const trimmed = searchTerm.trim();
        if (trimmed.length < 2) {
            setResults([]);
            setHasSearched(false);
            setIsLoading(false);
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            return;
        }

        const timer = setTimeout(() => {
            performSearch(trimmed, selectedCategory, activeCity?.id);
        }, 300);

        return () => {
            clearTimeout(timer);
        };
    }, [searchTerm, selectedCategory, activeCity?.id, isModalOpen, performSearch]);

    const handleSelectResult = (item: ISearchItem) => {
        setIsModalOpen(false);
        if (item.route) {
            navigate(item.route);
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (results.length > 0) {
                setSelectedIndex((prev) => (prev + 1 < results.length ? prev + 1 : 0));
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (results.length > 0) {
                setSelectedIndex((prev) => (prev - 1 >= 0 ? prev - 1 : results.length - 1));
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (results[selectedIndex]) {
                handleSelectResult(results[selectedIndex]);
            }
        }
    };

    const isMac =
        typeof navigator !== 'undefined' &&
        /Mac|iPod|iPhone|iPad/.test(
            (navigator as any)?.userAgentData?.platform || navigator?.platform || navigator?.userAgent || ''
        );

    return (
        <>
            <div
                className={`navbar-search-trigger ${isDark ? 'navbar-search-trigger-dark' : ''}`}
                onClick={() => setIsModalOpen(true)}
                role="button"
                tabIndex={0}
            >
                <div className="navbar-search-trigger-left">
                    <SearchOutlined className="navbar-search-icon" />
                    <span className="navbar-search-placeholder">
                        Buscar no sistema...
                    </span>
                </div>
                <span className="navbar-search-kbd">
                    {isMac ? '⌘K' : 'Ctrl+K'}
                </span>
            </div>

            <Modal
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                closable={false}
                destroyOnClose
                centered
                width={780}
                className={`global-search-modal ${isDark ? 'global-search-modal-dark' : ''}`}
                styles={{
                    body: {
                        padding: 0,
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 12,
                        overflow: 'hidden',
                    },
                }}
            >
                <div className="search-modal-input-wrap">
                    {isLoading ? (
                        <LoadingOutlined className="search-modal-input-spinner" />
                    ) : (
                        <SearchOutlined className="search-modal-input-icon" />
                    )}
                    <input
                        ref={inputRef}
                        type="text"
                        className="search-modal-input"
                        placeholder="Buscar por nome, CPF, CNPJ, telefone, e-mail..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setSelectedIndex(0);
                        }}
                        onKeyDown={handleInputKeyDown}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="search-modal-clear-btn"
                            onClick={() => {
                                setSearchTerm('');
                                setResults([]);
                                setHasSearched(false);
                            }}
                            title="Limpar busca"
                        >
                            ✕
                        </button>
                    )}
                    <span className="search-modal-esc-hint">ESC</span>
                </div>

                <div className="search-modal-categories-bar">
                    {categories.map((cat) => {
                        const isSelected = selectedCategory === cat.value;
                        return (
                            <button
                                key={cat.value}
                                type="button"
                                className={`search-category-pill ${isSelected ? 'active' : ''}`}
                                onClick={() => {
                                    setSelectedCategory(cat.value);
                                    setSelectedIndex(0);
                                }}
                            >
                                {cat.icon}
                                <span>{cat.label}</span>
                            </button>
                        );
                    })}
                </div>
                <div className="search-modal-results-container">
                    {isLoading && results.length === 0 ? (
                        <div className="search-modal-loading">
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 28 }} spin />} />
                            <span>Buscando resultados em tempo real...</span>
                        </div>
                    ) : results.length > 0 ? (
                        results.map((item, index) => {
                            const isSelected = index === selectedIndex;
                            return (
                                <div
                                    key={item.id}
                                    className={`search-result-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleSelectResult(item)}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                >
                                    <div className="search-result-icon-box">
                                        {item.icon}
                                    </div>
                                    <div className="search-result-info">
                                        <div className="search-result-title-row">
                                            <span className="search-result-title">{item.title}</span>
                                            <span className={`search-badge search-badge-${item.categoryKey}`}>
                                                {item.category}
                                            </span>
                                        </div>
                                        <div className="search-result-subtitle">{item.subtitle}</div>
                                    </div>
                                    <RightOutlined className="search-result-arrow" />
                                </div>
                            );
                        })
                    ) : hasSearched ? (
                        <div className="search-modal-empty">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span>
                                        Nenhum resultado encontrado para "<strong>{searchTerm}</strong>"
                                    </span>
                                }
                            />
                        </div>
                    ) : (
                        <div className="search-modal-initial-prompt">
                            <div className="search-modal-prompt-title">
                                Busca Rápida no Painel
                            </div>
                            <div className="search-modal-prompt-desc">
                                Digite ao menos 2 caracteres para pesquisar instantaneamente no sistema.
                            </div>
                        </div>
                    )}
                </div>

                <div className="search-modal-footer">
                    <div className="search-footer-shortcuts">
                        <span><kbd>↑</kbd> <kbd>↓</kbd> para navegar</span>
                        <span><kbd>↵</kbd> para selecionar</span>
                        <span><kbd>esc</kbd> para fechar</span>
                    </div>
                    <span className="search-footer-count">
                        {results.length} {results.length === 1 ? 'resultado' : 'resultados'}
                    </span>
                </div>
            </Modal>
        </>
    );
}
